import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, User, UserX } from "lucide-react";
import { api, fileUrl, errMsg } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import SearchableSelect from "../components/SearchableSelect";
import { toast } from "sonner";
import { isoToDisplay } from "../components/DateInput";

const firstOfMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; };
const today = () => new Date().toISOString().slice(0, 10);

export default function PresensiTidakHadirPage() {
  const navigate = useNavigate();
  const [opts, setOpts] = useState({ jenis_ibadah: [], komisi: [] });
  const [dari, setDari] = useState(firstOfMonth());
  const [sampai, setSampai] = useState(today());
  const [jenis, setJenis] = useState("");
  const [komisi, setKomisi] = useState("");
  const [kk, setKk] = useState("");
  const [d, setD] = useState({ items: [], total: 0, total_sesi: 0 });

  useEffect(() => { api.get("/presensi/options").then((r) => setOpts(r.data)).catch(() => {}); }, []);
  const load = useCallback(async () => {
    if (!dari || !sampai) return;
    const params = { dari, sampai };
    if (jenis) params.jenis_ibadah = jenis;
    if (komisi) params.komisi = komisi;
    if (kk) params.nomor_kk = kk;
    try { const r = await api.get("/presensi/tidak-hadir", { params }); setD(r.data); } catch (e) { toast.error(errMsg(e)); }
  }, [dari, sampai, jenis, komisi, kk]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="max-w-4xl mx-auto space-y-5" data-testid="tidak-hadir-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="rounded-xl" onClick={() => navigate("/presensi")} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
        <div><h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900">Jemaat Tidak Hadir</h1><p className="text-xs sm:text-sm text-slate-500">Data untuk Tim Kunjungan · <span data-testid="th-total">{d.total}</span> jemaat dari {d.total_sesi} sesi</p></div>
      </div>
      <Card className="p-3 rounded-2xl grid grid-cols-2 sm:grid-cols-3 gap-2">
        <div><Label className="text-xs text-slate-500 mb-1 block">Dari</Label><Input type="date" value={dari} onChange={(e) => setDari(e.target.value)} className="min-h-[44px] rounded-xl" data-testid="th-dari" /></div>
        <div><Label className="text-xs text-slate-500 mb-1 block">Sampai</Label><Input type="date" value={sampai} onChange={(e) => setSampai(e.target.value)} className="min-h-[44px] rounded-xl" data-testid="th-sampai" /></div>
        <div><Label className="text-xs text-slate-500 mb-1 block">Nomor KK</Label><Input value={kk} onChange={(e) => setKk(e.target.value)} placeholder="Semua KK" className="min-h-[44px] rounded-xl font-mono" data-testid="th-kk" /></div>
        <div><Label className="text-xs text-slate-500 mb-1 block">Jenis Ibadah</Label><SearchableSelect options={opts.jenis_ibadah} value={jenis} onChange={setJenis} placeholder="Semua jenis" testId="th-jenis" /></div>
        <div><Label className="text-xs text-slate-500 mb-1 block">Komisi</Label><SearchableSelect options={opts.komisi} value={komisi} onChange={setKomisi} placeholder="Semua komisi" testId="th-komisi" /></div>
      </Card>
      {d.items.length === 0 ? (
        <Card className="p-10 text-center rounded-2xl" data-testid="th-empty"><UserX className="h-10 w-10 mx-auto text-slate-300 mb-2" /><p className="text-slate-500">Tidak ada jemaat berstatus Tidak Hadir pada periode ini.</p></Card>
      ) : (
        <div className="space-y-2" data-testid="th-list">
          {d.items.map((j) => (
            <Card key={j.jemaat_id} className="p-3 rounded-2xl" data-testid={`th-row-${j.jemaat_id}`}>
              <div className="flex items-start gap-3">
                <Avatar className="h-12 w-12 rounded-xl border border-slate-200 shrink-0 cursor-pointer" onClick={() => navigate(`/jemaat/${j.jemaat_id}`)}>
                  {j.foto_path ? <AvatarImage src={fileUrl(j.foto_path)} alt={j.nama_lengkap} className="object-cover" /> : null}
                  <AvatarFallback className="rounded-xl bg-[hsl(224,64%,33%)]/10 text-[hsl(224,64%,33%)] text-xs font-semibold">{(j.nama_lengkap || "").trim().split(/\s+/).filter(Boolean).slice(0, 2).map((k) => k[0].toUpperCase()).join("") || <User className="h-5 w-5" />}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-slate-900 truncate">{j.nama_lengkap}</div>
                    <span className="text-xs px-2.5 py-1 rounded-full bg-red-50 text-red-700 font-bold whitespace-nowrap" data-testid={`th-freq-${j.jemaat_id}`}>{j.frekuensi_tidak_hadir}× tidak hadir</span>
                  </div>
                  <div className="text-xs text-slate-500 font-mono">{j.nij}{j.nomor_kk ? ` · KK ${j.nomor_kk}` : ""}</div>
                  <div className="text-[11px] text-slate-500">{j.komisi || "Tanpa komisi"}</div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {j.daftar_ibadah.slice(0, 6).map((s) => (
                      <button key={s.sesi_id} onClick={() => navigate(`/presensi/${s.sesi_id}`)} className="text-[11px] px-2 py-1 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200">{isoToDisplay(s.tanggal)} · {s.komisi || s.jenis_ibadah}</button>
                    ))}
                    {j.daftar_ibadah.length > 6 && <span className="text-[11px] text-slate-400 px-1 py-1">+{j.daftar_ibadah.length - 6} lainnya</span>}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
